import React from "react"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography
} from "@mui/material"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import StarIcon from "@mui/icons-material/Star"
import { Pack } from "@/shared/constants/plans"
import { useSnackbar } from "@/shared/context/SnackbarContext"

export interface PackCardProps {
  pack: Pack
  loading: boolean
  /**
   * If provided, a purchase button is rendered and this callback is invoked on click.
   * Omit for display-only contexts (e.g. the landing page pricing section).
   */
  onPurchase?: () => Promise<void>
}

/**
 * Shared credit pack card used in both the upgrade dialog and the landing page.
 * Shows a purchase button when `onPurchase` is supplied; display-only otherwise.
 */
const PackCard: React.FC<PackCardProps> = ({ loading, pack, onPurchase }) => {
  const { showSnackbar } = useSnackbar()

  const isPopular = pack.badge === "Most Popular"
  const isBestValue = pack.badge === "Best Value"
  const isHighlighted = isPopular || isBestValue

  const handlePurchase = async () => {
    if (!onPurchase) return
    try {
      await onPurchase()
    } catch (error) {
      showSnackbar((error as Error)?.message ?? "Failed to start checkout. Please try again.", "error")
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
        border: isHighlighted ? "2px solid" : "1px solid",
        borderColor: isPopular ? "primary.main" : isBestValue ? "secondary.main" : "divider",
        boxShadow: isHighlighted ? 4 : 0,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 6 }
      }}>
      {pack.badge && (
        <Box sx={{ position: "absolute", top: -2, left: "50%", transform: "translateX(-50%)" }}>
          <Chip
            icon={<StarIcon fontSize="small" />}
            label={pack.badge}
            color={isPopular ? "primary" : "secondary"}
            size="small"
            sx={{ fontWeight: 700, px: 1 }}
          />
        </Box>
      )}
      <Stack gap={1} mb={2}>
        <Typography variant="h6" fontWeight={700}>
          {pack.name}
        </Typography>
        <Stack direction="row" alignItems="baseline" gap={0.5}>
          <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}>
            {pack.razorpayAmountPaise === 0 ? "Free" : `₹${pack.razorpayAmountPaise / 100}`}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {pack.billingLabel}
        </Typography>
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      <List dense disablePadding sx={{ flex: 1 }}>
        {pack.features.map((feature, idx) => (
          <ListItem key={idx} disableGutters disablePadding sx={{ py: 0.4 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {feature.included ? (
                <CheckCircleIcon fontSize="small" color="success" />
              ) : (
                <CancelIcon fontSize="small" color="disabled" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={feature.label}
              slotProps={{
                primary: {
                  variant: "body2",
                  color: feature.included ? "text.primary" : "text.disabled"
                }
              }}
            />
          </ListItem>
        ))}
      </List>
      {onPurchase && (
        <Button
          variant={isPopular ? "contained" : "outlined"}
          color={isBestValue ? "secondary" : "primary"}
          fullWidth
          onClick={handlePurchase}
          disabled={loading}
          sx={{ mt: 2 }}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {pack.ctaLabel}
        </Button>
      )}
    </Paper>
  )
}

export default PackCard
